import { z } from "zod";

export const MetabaseJWTTokenSchema = z.object({
  jwt: z.string(),
  expires_at: z.number(),
  cached_at: z.number(),
});

export const MetabaseDashboardSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  tenant_id: z.string().optional(), // For security validation
});

export const MetabaseDashboardsResponseSchema = z.object({
  dashboards: z.array(MetabaseDashboardSchema),
  count: z.number(),
  page: z.number().optional(),
  page_size: z.number().optional(),
  total_pages: z.number().optional(),
});

export const MetabaseDashboardsRequestSchema = z.object({
  page: z.number().min(1).optional().default(1),
  page_size: z.number().min(1).max(100).optional().default(20),
  search: z.string().optional(),
});

// Individual Space/Dashboard item schema (using dashboard API structure)
export const SpaceSchema = z.object({
  // Basic identification
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  tags: z.array(z.string()).nullable(),
  attributes: z.record(z.unknown()).nullable(),

  // Additional fields that will be added to the dashboard API response
  dashboard_id: z.number().optional(), // Metabase dashboard ID
  collection_id: z.number().nullable().optional(),
  collection_name: z.string().nullable().optional(),
  dashboard_metadata: z.record(z.unknown()).nullable().optional(),
  collection_metadata: z.record(z.unknown()).nullable().optional(),

  // Creator information (extracted from dashboard_metadata)
  creator: z
    .object({
      id: z.number().optional(),
      email: z.string().nullable().optional(),
      first_name: z.string().nullable().optional(),
      last_name: z.string().nullable().optional(),
      common_name: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),

  // Usage statistics (extracted from dashboard_metadata)
  view_count: z.number().optional(),
  last_viewed_at: z.string().optional(),

  // Tenant information
  tenant_name: z.string().optional(),
});

// Collection information for filters
export const CollectionFilterSchema = z.object({
  id: z.number(),
  name: z.string(),
  count: z.number(), // Number of spaces in this collection
});

// Response wrapper schema (using dashboard API structure)
export const SpacesResponseSchema = z.object({
  dashboards: z.array(SpaceSchema), // Using 'dashboards' key to match existing API
  count: z.number(), // Number of items in current page
  total: z.number(), // Total number of items across all pages
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
  has_next: z.boolean(),
  has_previous: z.boolean(),

  // Available filter options (for dropdowns) - these will be added to the API response
  available_tags: z.array(z.string()).optional(),
  available_collections: z.array(CollectionFilterSchema).optional(),
});

// Request parameters schema (using dashboard API structure)
export const SpacesRequestSchema = z.object({
  // Pagination
  page: z.number().min(1).optional().default(1),
  page_size: z.number().min(1).max(100).optional().default(20),

  // Search
  search: z.string().optional(),

  // Filtering - these will be added to the dashboard API
  tags: z.array(z.string()).optional(), // Filter by specific tags
  collection_ids: z.array(z.number()).optional(), // Filter by collection IDs
  collection_names: z.array(z.string()).optional(), // Filter by collection names

  // Date filtering
  created_after: z.string().optional(), // ISO date string
  created_before: z.string().optional(), // ISO date string
  updated_after: z.string().optional(), // ISO date string
  updated_before: z.string().optional(), // ISO date string

  // Sorting
  sort_by: z
    .enum(["name", "created_at", "updated_at", "collection_name"])
    .optional()
    .default("updated_at"),
  sort_order: z.enum(["asc", "desc"]).optional().default("desc"),

  // Additional filters
  has_description: z.boolean().optional(), // Filter spaces with/without descriptions
  archived: z.boolean().optional().default(false), // Include/exclude archived spaces
});

// Sync all dashboards response schema
export const SyncAllDashboardsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  error: z.string().optional(),
  batch_id: z.number().nullable().optional(),
  tenants_processed: z.number().optional(),
  dashboards_synced: z.number().optional(),
  dashboards_updated: z.number().optional(),
  per_tenant: z.record(z.record(z.number())).optional(),
});

// Type exports
export type Space = z.infer<typeof SpaceSchema>;
export type CollectionFilter = z.infer<typeof CollectionFilterSchema>;
export type SpacesResponse = z.infer<typeof SpacesResponseSchema>;
export type SpacesRequest = z.infer<typeof SpacesRequestSchema>;
export type MetabaseJWTToken = z.infer<typeof MetabaseJWTTokenSchema>;
export type MetabaseDashboard = z.infer<typeof MetabaseDashboardSchema>;
export type MetabaseDashboardsResponse = z.infer<typeof MetabaseDashboardsResponseSchema>;
export type MetabaseDashboardsRequest = z.infer<typeof MetabaseDashboardsRequestSchema>;
export type SyncAllDashboardsResponse = z.infer<typeof SyncAllDashboardsResponseSchema>;
